/**
 * Redirect-after-login, with no open redirect (00-contracts §14.2).
 *
 * Only a same-origin *relative* path survives. An absolute URL, a protocol-relative `//host`,
 * a backslash variant that some browsers normalise to `//`, or anything carrying a control
 * character is discarded in favour of the app root. This is the only place that decides it.
 */
export const DEFAULT_REDIRECT = '/';

/**
 * Header-splitting and path-smuggling both begin with a control character. Checked by code
 * point rather than by regular expression, so the rule is readable and the source stays ASCII.
 */
function hasControlCharacter(value: string): boolean {
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/** The escape character itself, spelled by code point so this file carries no raw escapes. */
const BACKSLASH = String.fromCharCode(92);

export function safeRedirect(raw: string | null | undefined): string {
  if (typeof raw !== 'string' || raw.length === 0) return DEFAULT_REDIRECT;
  if (raw.length > 512) return DEFAULT_REDIRECT;
  if (hasControlCharacter(raw)) return DEFAULT_REDIRECT;

  // A backslash never belongs in an internal path, and some browsers normalise it to '/',
  // which turns "/\evil.com" into "//evil.com". Reject rather than rewrite.
  if (raw.includes(BACKSLASH)) return DEFAULT_REDIRECT;
  if (!raw.startsWith('/')) return DEFAULT_REDIRECT;
  if (raw.startsWith('//')) return DEFAULT_REDIRECT;
  return raw;
}

/** Builds the `?redirect=` value for a sign-in bounce, preserving query and hash. */
export function toRedirectParam(href: string): string {
  return safeRedirect(href);
}
